import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  Camera,
  CheckCircle2,
  Eye,
  EyeOff,
  Globe2,
  ImagePlus,
  KeyRound,
  Languages,
  Loader2,
  LockKeyhole,
  Mail,
  MapPin,
  MessageCircleMore,
  Phone,
  Save,
  ShieldCheck,
  Store,
  Text,
  Upload,
  UserRound,
} from 'lucide-react';

import accountSettingsApi from '../../api/accountSettingsApi';
import { getApiErrorMessage } from '../../api/axiosClient';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

import '../../styles/account/account-settings.css';

const roleTranslationKeys = {
  SUPER_ADMIN: 'roles.superAdmin',
  RESTAURANT_MANAGER: 'roles.restaurantManager',
  MANAGER: 'roles.restaurantManager',
  SERVEUR: 'roles.serveur',
  WAITER: 'roles.serveur',
  CUISINE: 'roles.cuisine',
  KITCHEN: 'roles.cuisine',
  CUISINER: 'roles.cuisine',
  CUISINIER: 'roles.cuisine',
  CUISINI: 'roles.cuisine',
};

const MANAGER_ROLES = [
  'MANAGER',
  'RESTAURANT_MANAGER',
];

const ESTABLISHMENT_TYPES = [
  'restaurant',
  'cafe',
  'snack',
  'bakery',
  'hotel',
  'riad',
  'other',
];

const LANGUAGE_OPTIONS = [
  'ar',
  'fr',
  'en',
  'es',
];

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key
    ? fallback
    : value;
};

const normalizeRole = (role) => {
  return String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, '_');
};

const extractRoleValue = (role) => {
  if (!role) {
    return '';
  }

  if (
    typeof role === 'string' ||
    typeof role === 'number'
  ) {
    return role;
  }

  if (typeof role === 'object') {
    return (
      role.name ||
      role.slug ||
      role.code ||
      role.value ||
      role.key ||
      role.role ||
      ''
    );
  }

  return '';
};

const collectRoleValues = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .flatMap((role) =>
        collectRoleValues(role),
      )
      .filter(Boolean);
  }

  const roleValue =
    extractRoleValue(value);

  return roleValue
    ? [roleValue]
    : [];
};

const getAccountRoles = (
  account = {},
  fallbackUser = {},
) => {
  const roleSources = [
    account?.roles,
    account?.role,
    fallbackUser?.roles,
    fallbackUser?.role,
    fallbackUser?.data?.roles,
    fallbackUser?.data?.role,
    fallbackUser?.user?.roles,
    fallbackUser?.user?.role,
  ];

  const roles =
    roleSources.flatMap((source) =>
      collectRoleValues(source),
    );

  const unique = [];

  roles.forEach((role) => {
    const normalized =
      normalizeRole(role);

    if (
      normalized &&
      !unique.some(
        (existingRole) =>
          normalizeRole(existingRole) ===
          normalized,
      )
    ) {
      unique.push(role);
    }
  });

  return unique;
};

const getInitials = (name = '') => {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) {
    return 'U';
  }

  return words
    .slice(0, 2)
    .map((word) =>
      word[0]?.toUpperCase(),
    )
    .join('');
};

const getFirstFieldError = (
  error,
  fallback,
) => {
  const errors =
    error?.response?.data?.errors;

  if (!errors) {
    return fallback;
  }

  const firstError =
    Object.values(errors)
      .flat()
      .find(Boolean);

  return firstError || fallback;
};

const buildBusinessState = (
  account = {},
  fallbackUser = {},
) => {
  const business =
    account?.business ||
    fallbackUser?.business ||
    {};

  const establishment =
    account?.establishment ||
    fallbackUser?.establishment ||
    {};

  return {
    business_name:
      business?.name || '',

    legal_name:
      business?.legal_name || '',

    business_email:
      business?.email || '',

    business_phone:
      business?.phone || '',

    website:
      business?.website || '',

    establishment_name:
      establishment?.name || '',

    type:
      establishment?.type ||
      'restaurant',

    default_language:
      establishment?.default_language ||
      business?.default_language ||
      'fr',

    establishment_email:
      establishment?.email || '',

    establishment_phone:
      establishment?.phone || '',

    whatsapp:
      establishment?.whatsapp || '',

    address:
      establishment?.address || '',

    description:
      establishment?.description || '',

    logo_url:
      establishment?.logo_url ||
      account?.logo_url ||
      fallbackUser?.logo_url ||
      '',

    cover_url:
      establishment?.cover_url ||
      '',
  };
};

function AccountSettings() {
  const { t } = useLanguage();

  const {
    user,
    refreshUser,
  } = useAuth();

  const {
    showAlert,
  } = useAlerts();

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    savingProfile,
    setSavingProfile,
  ] = useState(false);

  const [
    savingBusiness,
    setSavingBusiness,
  ] = useState(false);

  const [
    savingPassword,
    setSavingPassword,
  ] = useState(false);

  const [
    showPasswords,
    setShowPasswords,
  ] = useState(false);

  const [
    profile,
    setProfile,
  ] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    roles: [],
    avatar_url: '',
    logo_url: '',
    establishment_id: null,
  });

  const [
    businessForm,
    setBusinessForm,
  ] = useState(() =>
    buildBusinessState(
      {},
      user,
    ),
  );

  const [
    avatarFile,
    setAvatarFile,
  ] = useState(null);

  const [
    logoFile,
    setLogoFile,
  ] = useState(null);

  const [
    coverFile,
    setCoverFile,
  ] = useState(null);

  const [
    passwordForm,
    setPasswordForm,
  ] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });

  const [direction, setDirection] =
    useState(
      document.documentElement.dir ||
        'ltr',
    );

  const accountRoles =
    useMemo(
      () =>
        getAccountRoles(
          {
            role: profile.role,
            roles: profile.roles,
          },
          user,
        ),
      [
        profile.role,
        profile.roles,
        user,
      ],
    );

  const normalizedRoles =
    useMemo(
      () =>
        accountRoles.map(
          (role) =>
            normalizeRole(role),
        ),
      [accountRoles],
    );

  const isManager =
    normalizedRoles.some(
      (role) =>
        MANAGER_ROLES.includes(
          role,
        ),
    );

  const roleLabel =
    useMemo(() => {
      const labels =
        accountRoles.map(
          (role) => {
            const roleKey =
              roleTranslationKeys[
                normalizeRole(
                  role,
                )
              ];

            return roleKey
              ? t(roleKey)
              : String(role);
          },
        );

      return labels.join(' · ');
    }, [
      accountRoles,
      t,
    ]);

  const initials =
    getInitials(
      profile.name ||
        user?.name ||
        '',
    );

  const avatarPreview =
    useMemo(() => {
      if (avatarFile) {
        return URL.createObjectURL(
          avatarFile,
        );
      }

      return (
        profile.avatar_url ||
        ''
      );
    }, [
      avatarFile,
      profile.avatar_url,
    ]);

  const logoPreview =
    useMemo(() => {
      if (logoFile) {
        return URL.createObjectURL(
          logoFile,
        );
      }

      return (
        businessForm.logo_url ||
        ''
      );
    }, [
      logoFile,
      businessForm.logo_url,
    ]);

  const coverPreview =
    useMemo(() => {
      if (coverFile) {
        return URL.createObjectURL(
          coverFile,
        );
      }

      return (
        businessForm.cover_url ||
        ''
      );
    }, [
      coverFile,
      businessForm.cover_url,
    ]);

  useEffect(() => {
    return () => {
      if (
        avatarPreview.startsWith(
          'blob:',
        )
      ) {
        URL.revokeObjectURL(
          avatarPreview,
        );
      }
    };
  }, [
    avatarPreview,
  ]);

  useEffect(() => {
    return () => {
      if (
        logoPreview.startsWith(
          'blob:',
        )
      ) {
        URL.revokeObjectURL(
          logoPreview,
        );
      }
    };
  }, [
    logoPreview,
  ]);

  useEffect(() => {
    return () => {
      if (
        coverPreview.startsWith(
          'blob:',
        )
      ) {
        URL.revokeObjectURL(
          coverPreview,
        );
      }
    };
  }, [
    coverPreview,
  ]);

  const syncAccount = (
    account = {},
  ) => {
    setProfile({
      name:
        account.name ||
        user?.name ||
        '',

      email:
        account.email ||
        user?.email ||
        '',

      phone:
        account.phone ||
        '',

      role:
        extractRoleValue(
          account.role ||
            user?.role,
        ),

      roles:
        getAccountRoles(
          account,
          user,
        ),

      avatar_url:
        account.avatar_url ||
        user?.avatar_url ||
        '',

      logo_url:
        account.logo_url ||
        account
          ?.establishment
          ?.logo_url ||
        user?.logo_url ||
        user
          ?.establishment
          ?.logo_url ||
        '',

      establishment_id:
        account.establishment_id ||
        account
          ?.establishment
          ?.id ||
        user?.establishment_id ||
        user
          ?.establishment
          ?.id ||
        null,
    });

    setBusinessForm(
      buildBusinessState(
        account,
        user,
      ),
    );
  };

  const loadAccount =
    async () => {
      try {
        setLoading(true);

        const response =
          await accountSettingsApi.getAccount();

        const account =
          response.data?.data ||
          {};

        syncAccount(
          account,
        );
      } catch (error) {
        showAlert({
          type: 'error',
          title:
            t('alerts.error'),
          message:
            getApiErrorMessage(
              error,
            ),
        });
      } finally {
        setLoading(false);
      }
    };

  const updateProfileField = (
    field,
    value,
  ) => {
    setProfile(
      (currentProfile) => ({
        ...currentProfile,
        [field]: value,
      }),
    );
  };

  const updateBusinessField = (
    field,
    value,
  ) => {
    setBusinessForm(
      (currentForm) => ({
        ...currentForm,
        [field]: value,
      }),
    );
  };

  const updatePasswordField = (
    field,
    value,
  ) => {
    setPasswordForm(
      (currentForm) => ({
        ...currentForm,
        [field]: value,
      }),
    );
  };

  const handleAvatarChange = (
    event,
  ) => {
    setAvatarFile(
      event.target.files?.[0] ||
        null,
    );
  };

  const handleLogoChange = (
    event,
  ) => {
    setLogoFile(
      event.target.files?.[0] ||
        null,
    );
  };

  const handleCoverChange = (
    event,
  ) => {
    setCoverFile(
      event.target.files?.[0] ||
        null,
    );
  };

  const handleProfileSubmit =
    async (event) => {
      event.preventDefault();

      try {
        setSavingProfile(
          true,
        );

        const formData =
          new FormData();

        formData.append(
          'name',
          profile.name.trim(),
        );

        formData.append(
          'email',
          profile.email.trim(),
        );

        formData.append(
          'phone',
          profile.phone.trim(),
        );

        if (avatarFile) {
          formData.append(
            'avatar',
            avatarFile,
          );
        }

        const response =
          await accountSettingsApi.updateProfile(
            formData,
          );

        const account =
          response.data?.data ||
          {};

        syncAccount(
          account,
        );

        setAvatarFile(
          null,
        );

        await refreshUser();

        showAlert({
          type: 'success',
          title:
            t(
              'alerts.success',
            ),
          message:
            translateFallback(
              t,
              'accountSettings.alerts.profileUpdated',
              'Account profile updated successfully.',
            ),
        });
      } catch (error) {
        showAlert({
          type: 'error',
          title:
            t(
              'alerts.error',
            ),
          message:
            getFirstFieldError(
              error,
              translateFallback(
                t,
                'accountSettings.alerts.profileError',
                'Unable to update the account profile.',
              ),
            ),
        });
      } finally {
        setSavingProfile(
          false,
        );
      }
    };

  const handleBusinessSubmit =
    async (event) => {
      event.preventDefault();

      if (!isManager) {
        return;
      }

      try {
        setSavingBusiness(
          true,
        );

        const formData =
          new FormData();

        Object.entries(
          businessForm,
        ).forEach(
          ([key, value]) => {
            if (
              key ===
                'logo_url' ||
              key ===
                'cover_url'
            ) {
              return;
            }

            formData.append(
              key,
              value ?? '',
            );
          },
        );

        if (logoFile) {
          formData.append(
            'logo',
            logoFile,
          );
        }

        if (coverFile) {
          formData.append(
            'cover',
            coverFile,
          );
        }

        const response =
          await accountSettingsApi.updateBusiness(
            formData,
          );

        const account =
          response.data?.data ||
          {};

        syncAccount(
          account,
        );

        setLogoFile(
          null,
        );

        setCoverFile(
          null,
        );

        await refreshUser();

        showAlert({
          type: 'success',
          title:
            t(
              'alerts.success',
            ),
          message:
            translateFallback(
              t,
              'accountSettings.alerts.businessUpdated',
              'Business information updated successfully.',
            ),
        });
      } catch (error) {
        showAlert({
          type: 'error',
          title:
            t(
              'alerts.error',
            ),
          message:
            getFirstFieldError(
              error,
              translateFallback(
                t,
                'accountSettings.alerts.businessError',
                'Unable to update the business information.',
              ),
            ),
        });
      } finally {
        setSavingBusiness(
          false,
        );
      }
    };

  const handlePasswordSubmit =
    async (event) => {
      event.preventDefault();

      if (
        passwordForm.password !==
        passwordForm.password_confirmation
      ) {
        showAlert({
          type: 'error',
          title:
            t(
              'alerts.error',
            ),
          message:
            translateFallback(
              t,
              'accountSettings.validation.passwordMismatch',
              'The password confirmation does not match.',
            ),
        });

        return;
      }

      try {
        setSavingPassword(
          true,
        );

        await accountSettingsApi.updatePassword(
          passwordForm,
        );

        setPasswordForm({
          current_password: '',
          password: '',
          password_confirmation:
            '',
        });

        showAlert({
          type: 'success',
          title:
            t(
              'alerts.success',
            ),
          message:
            translateFallback(
              t,
              'accountSettings.alerts.passwordUpdated',
              'Password updated successfully.',
            ),
        });
      } catch (error) {
        showAlert({
          type: 'error',
          title:
            t(
              'alerts.error',
            ),
          message:
            getFirstFieldError(
              error,
              translateFallback(
                t,
                'accountSettings.alerts.passwordError',
                'Unable to update the password.',
              ),
            ),
        });
      } finally {
        setSavingPassword(
          false,
        );
      }
    };

  useEffect(() => {
    const updateDirection = () => {
      setDirection(
        document.documentElement.dir ||
          'ltr',
      );
    };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          'dir',
          'lang',
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    loadAccount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <main
        className={`account-settings account-settings--loading ${
          direction === 'rtl'
            ? 'account-settings--rtl'
            : 'account-settings--ltr'
        }`}
        dir={direction}
      >
        <Loader2
          size={34}
        />

        <p>
          {translateFallback(
            t,
            'accountSettings.loading',
            'Loading account settings...',
          )}
        </p>
      </main>
    );
  }

  return (
    <main
      className={`account-settings ${
        direction === 'rtl'
          ? 'account-settings--rtl'
          : 'account-settings--ltr'
      }`}
      dir={direction}
    >
      <section className="account-settings__hero">
        <div className="account-settings__identity">
          <span className="account-settings__avatar">
            {avatarPreview ? (
              <img
                src={
                  avatarPreview
                }
                alt={
                  profile.name
                }
              />
            ) : (
              initials
            )}
          </span>

          <div>
            <p>
              {translateFallback(
                t,
                'accountSettings.eyebrow',
                'Account',
              )}
            </p>

            <h1>
              {translateFallback(
                t,
                'accountSettings.title',
                'Account settings',
              )}
            </h1>

            <span>
              <ShieldCheck
                size={16}
              />

              {roleLabel}
            </span>
          </div>
        </div>

        <div className="account-settings__secure">
          <CheckCircle2
            size={18}
          />

          {translateFallback(
            t,
            'accountSettings.secureAccount',
            'Secure account',
          )}
        </div>
      </section>

      {isManager && (
        <form
          className="account-settings-card account-settings-card--business"
          onSubmit={
            handleBusinessSubmit
          }
        >
          <div className="account-settings-card__head">
            <span>
              <Store
                size={21}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  'accountSettings.businessTitle',
                  'Business information',
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  'accountSettings.businessDescription',
                  'Manage the restaurant information shown in the sidebar and public menu.',
                )}
              </p>
            </div>
          </div>

          <div className="account-settings-media-grid">
            <div className="account-settings-media">
              <div className="account-settings-media__preview account-settings-media__preview--logo">
                {logoPreview ? (
                  <img
                    src={
                      logoPreview
                    }
                    alt={translateFallback(
                      t,
                      'accountSettings.fields.businessLogo',
                      'Business logo',
                    )}
                  />
                ) : (
                  <Store
                    size={30}
                  />
                )}
              </div>

              <div className="account-settings-media__content">
                <strong>
                  {translateFallback(
                    t,
                    'accountSettings.fields.businessLogo',
                    'Business logo',
                  )}
                </strong>

                <span>
                  {translateFallback(
                    t,
                    'accountSettings.fields.businessLogoHint',
                    'Shown in the sidebar, menu branding and other logo areas.',
                  )}
                </span>

                <input
                  id="account-business-logo"
                  className="account-settings-media__input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={
                    handleLogoChange
                  }
                />

                <label
                  htmlFor="account-business-logo"
                  className="account-settings-media__button"
                >
                  <ImagePlus
                    size={16}
                  />

                  {translateFallback(
                    t,
                    'accountSettings.actions.chooseLogo',
                    'Choose logo',
                  )}
                </label>
              </div>
            </div>

            <div className="account-settings-media">
              <div className="account-settings-media__preview account-settings-media__preview--cover">
                {coverPreview ? (
                  <img
                    src={
                      coverPreview
                    }
                    alt={translateFallback(
                      t,
                      'accountSettings.fields.coverImage',
                      'Cover image',
                    )}
                  />
                ) : (
                  <Upload
                    size={30}
                  />
                )}
              </div>

              <div className="account-settings-media__content">
                <strong>
                  {translateFallback(
                    t,
                    'accountSettings.fields.coverImage',
                    'Cover image',
                  )}
                </strong>

                <span>
                  {translateFallback(
                    t,
                    'accountSettings.fields.coverImageHint',
                    'Used in the public menu and establishment presentation.',
                  )}
                </span>

                <input
                  id="account-business-cover"
                  className="account-settings-media__input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={
                    handleCoverChange
                  }
                />

                <label
                  htmlFor="account-business-cover"
                  className="account-settings-media__button"
                >
                  <ImagePlus
                    size={16}
                  />

                  {translateFallback(
                    t,
                    'accountSettings.actions.chooseCover',
                    'Choose cover',
                  )}
                </label>
              </div>
            </div>
          </div>

          <div className="account-settings-form-grid">
            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.businessName',
                  'Business name',
                )}
              </span>

              <div>
                <Building2
                  size={18}
                />

                <input
                  type="text"
                  value={
                    businessForm.business_name
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'business_name',
                      event.target
                        .value,
                    )
                  }
                  required
                />
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.legalName',
                  'Legal name',
                )}
              </span>

              <div>
                <Building2
                  size={18}
                />

                <input
                  type="text"
                  value={
                    businessForm.legal_name
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'legal_name',
                      event.target
                        .value,
                    )
                  }
                  placeholder={translateFallback(
                    t,
                    'accountSettings.fields.optionalPlaceholder',
                    'Optional',
                  )}
                />
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.businessEmail',
                  'Business email',
                )}
              </span>

              <div>
                <Mail
                  size={18}
                />

                <input
                  type="email"
                  dir="ltr"
                  value={
                    businessForm.business_email
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'business_email',
                      event.target
                        .value,
                    )
                  }
                />
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.businessPhone',
                  'Business phone',
                )}
              </span>

              <div>
                <Phone
                  size={18}
                />

                <input
                  type="tel"
                  dir="ltr"
                  value={
                    businessForm.business_phone
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'business_phone',
                      event.target
                        .value,
                    )
                  }
                />
              </div>
            </label>

            <label className="account-settings-field account-settings-field--full">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.website',
                  'Website',
                )}
              </span>

              <div>
                <Globe2
                  size={18}
                />

                <input
                  type="text"
                  dir="ltr"
                  value={
                    businessForm.website
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'website',
                      event.target
                        .value,
                    )
                  }
                  placeholder="https://example.com"
                />
              </div>
            </label>
          </div>

          <div className="account-settings-card__head account-settings-card__head--section">
            <span>
              <Store
                size={21}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  'accountSettings.establishmentTitle',
                  'Establishment details',
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  'accountSettings.establishmentDescription',
                  'These details are used in the menu, sidebar and customer-facing pages.',
                )}
              </p>
            </div>
          </div>

          <div className="account-settings-form-grid">
            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.establishmentName',
                  'Establishment name',
                )}
              </span>

              <div>
                <Store
                  size={18}
                />

                <input
                  type="text"
                  value={
                    businessForm.establishment_name
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'establishment_name',
                      event.target
                        .value,
                    )
                  }
                  required
                />
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.establishmentType',
                  'Establishment type',
                )}
              </span>

              <div>
                <Store
                  size={18}
                />

                <select
                  value={
                    businessForm.type
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'type',
                      event.target
                        .value,
                    )
                  }
                >
                  {ESTABLISHMENT_TYPES.map(
                    (type) => (
                      <option
                        key={
                          type
                        }
                        value={
                          type
                        }
                      >
                        {translateFallback(
                          t,
                          `accountSettings.types.${type}`,
                          type,
                        )}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.defaultLanguage',
                  'Default language',
                )}
              </span>

              <div>
                <Languages
                  size={18}
                />

                <select
                  value={
                    businessForm.default_language
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'default_language',
                      event.target
                        .value,
                    )
                  }
                >
                  {LANGUAGE_OPTIONS.map(
                    (locale) => (
                      <option
                        key={
                          locale
                        }
                        value={
                          locale
                        }
                      >
                        {translateFallback(
                          t,
                          `accountSettings.languages.${locale}`,
                          locale.toUpperCase(),
                        )}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.establishmentEmail',
                  'Establishment email',
                )}
              </span>

              <div>
                <Mail
                  size={18}
                />

                <input
                  type="email"
                  dir="ltr"
                  value={
                    businessForm.establishment_email
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'establishment_email',
                      event.target
                        .value,
                    )
                  }
                />
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.establishmentPhone',
                  'Establishment phone',
                )}
              </span>

              <div>
                <Phone
                  size={18}
                />

                <input
                  type="tel"
                  dir="ltr"
                  value={
                    businessForm.establishment_phone
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'establishment_phone',
                      event.target
                        .value,
                    )
                  }
                />
              </div>
            </label>

            <label className="account-settings-field">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.whatsapp',
                  'WhatsApp',
                )}
              </span>

              <div>
                <MessageCircleMore
                  size={18}
                />

                <input
                  type="tel"
                  dir="ltr"
                  value={
                    businessForm.whatsapp
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'whatsapp',
                      event.target
                        .value,
                    )
                  }
                />
              </div>
            </label>

            <label className="account-settings-field account-settings-field--full">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.address',
                  'Address',
                )}
              </span>

              <div>
                <MapPin
                  size={18}
                />

                <input
                  type="text"
                  value={
                    businessForm.address
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'address',
                      event.target
                        .value,
                    )
                  }
                />
              </div>
            </label>

            <label className="account-settings-field account-settings-field--full">
              <span>
                {translateFallback(
                  t,
                  'accountSettings.fields.description',
                  'Description',
                )}
              </span>

              <div className="account-settings-field__textarea">
                <Text
                  size={18}
                />

                <textarea
                  rows={5}
                  value={
                    businessForm.description
                  }
                  onChange={(
                    event,
                  ) =>
                    updateBusinessField(
                      'description',
                      event.target
                        .value,
                    )
                  }
                />
              </div>
            </label>
          </div>

          <button
            type="submit"
            className="account-settings-button account-settings-button--primary"
            disabled={
              savingBusiness
            }
          >
            {savingBusiness ? (
              <Loader2
                size={17}
              />
            ) : (
              <Save
                size={17}
              />
            )}

            {translateFallback(
              t,
              'accountSettings.actions.saveBusiness',
              'Save business information',
            )}
          </button>
        </form>
      )}

      <section className="account-settings__grid">
        <form
          className="account-settings-card"
          onSubmit={
            handleProfileSubmit
          }
        >
          <div className="account-settings-card__head">
            <span>
              <UserRound
                size={21}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  'accountSettings.profileTitle',
                  'Profile information',
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  'accountSettings.profileDescription',
                  'Update your name, email and contact information.',
                )}
              </p>
            </div>
          </div>

          <div className="account-settings-media-grid account-settings-media-grid--single">
            <div className="account-settings-media">
              <div className="account-settings-media__preview account-settings-media__preview--avatar">
                {avatarPreview ? (
                  <img
                    src={
                      avatarPreview
                    }
                    alt={translateFallback(
                      t,
                      'accountSettings.fields.profilePhoto',
                      'Profile photo',
                    )}
                  />
                ) : (
                  <UserRound
                    size={30}
                  />
                )}
              </div>

              <div className="account-settings-media__content">
                <strong>
                  {translateFallback(
                    t,
                    'accountSettings.fields.profilePhoto',
                    'Profile photo',
                  )}
                </strong>

                <span>
                  {translateFallback(
                    t,
                    'accountSettings.fields.profilePhotoHint',
                    'Shown in your account and profile areas. JPG, PNG or WebP, max 4 MB.',
                  )}
                </span>

                <input
                  id="account-profile-avatar"
                  className="account-settings-media__input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={
                    handleAvatarChange
                  }
                />

                <label
                  htmlFor="account-profile-avatar"
                  className="account-settings-media__button"
                >
                  <Camera
                    size={16}
                  />

                  {translateFallback(
                    t,
                    'accountSettings.actions.choosePhoto',
                    'Choose photo',
                  )}
                </label>
              </div>
            </div>
          </div>

          <label className="account-settings-field">
            <span>
              {translateFallback(
                t,
                'accountSettings.fields.name',
                'Full name',
              )}
            </span>

            <div>
              <UserRound
                size={18}
              />

              <input
                type="text"
                value={
                  profile.name
                }
                onChange={(
                  event,
                ) =>
                  updateProfileField(
                    'name',
                    event.target
                      .value,
                  )
                }
                required
              />
            </div>
          </label>

          <label className="account-settings-field">
            <span>
              {translateFallback(
                t,
                'accountSettings.fields.email',
                'Email address',
              )}
            </span>

            <div>
              <Mail
                size={18}
              />

              <input
                type="email"
                dir="ltr"
                value={
                  profile.email
                }
                onChange={(
                  event,
                ) =>
                  updateProfileField(
                    'email',
                    event.target
                      .value,
                  )
                }
                required
              />
            </div>
          </label>

          <label className="account-settings-field">
            <span>
              {translateFallback(
                t,
                'accountSettings.fields.phone',
                'Phone',
              )}
            </span>

            <div>
              <Phone
                size={18}
              />

              <input
                type="tel"
                dir="ltr"
                value={
                  profile.phone
                }
                onChange={(
                  event,
                ) =>
                  updateProfileField(
                    'phone',
                    event.target
                      .value,
                  )
                }
                placeholder={translateFallback(
                  t,
                  'accountSettings.fields.phonePlaceholder',
                  'Optional',
                )}
              />
            </div>
          </label>

          <button
            type="submit"
            className="account-settings-button account-settings-button--primary"
            disabled={
              savingProfile
            }
          >
            {savingProfile ? (
              <Loader2
                size={17}
              />
            ) : (
              <Save
                size={17}
              />
            )}

            {translateFallback(
              t,
              'accountSettings.actions.saveProfile',
              'Save profile',
            )}
          </button>
        </form>

        <form
          className="account-settings-card"
          onSubmit={
            handlePasswordSubmit
          }
        >
          <div className="account-settings-card__head">
            <span>
              <LockKeyhole
                size={21}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  'accountSettings.passwordTitle',
                  'Security',
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  'accountSettings.passwordDescription',
                  'Change your password to keep your account protected.',
                )}
              </p>
            </div>
          </div>

          <label className="account-settings-field">
            <span>
              {translateFallback(
                t,
                'accountSettings.fields.currentPassword',
                'Current password',
              )}
            </span>

            <div>
              <KeyRound
                size={18}
              />

              <input
                type={
                  showPasswords
                    ? 'text'
                    : 'password'
                }
                value={
                  passwordForm.current_password
                }
                onChange={(
                  event,
                ) =>
                  updatePasswordField(
                    'current_password',
                    event.target
                      .value,
                  )
                }
                required
              />
            </div>
          </label>

          <label className="account-settings-field">
            <span>
              {translateFallback(
                t,
                'accountSettings.fields.newPassword',
                'New password',
              )}
            </span>

            <div>
              <LockKeyhole
                size={18}
              />

              <input
                type={
                  showPasswords
                    ? 'text'
                    : 'password'
                }
                value={
                  passwordForm.password
                }
                onChange={(
                  event,
                ) =>
                  updatePasswordField(
                    'password',
                    event.target
                      .value,
                  )
                }
                minLength={8}
                required
              />
            </div>
          </label>

          <label className="account-settings-field">
            <span>
              {translateFallback(
                t,
                'accountSettings.fields.confirmPassword',
                'Confirm password',
              )}
            </span>

            <div>
              <LockKeyhole
                size={18}
              />

              <input
                type={
                  showPasswords
                    ? 'text'
                    : 'password'
                }
                value={
                  passwordForm.password_confirmation
                }
                onChange={(
                  event,
                ) =>
                  updatePasswordField(
                    'password_confirmation',
                    event.target
                      .value,
                  )
                }
                minLength={8}
                required
              />
            </div>
          </label>

          <button
            type="button"
            className="account-settings-password-toggle"
            onClick={() =>
              setShowPasswords(
                (
                  currentValue,
                ) =>
                  !currentValue,
              )
            }
          >
            {showPasswords ? (
              <EyeOff
                size={16}
              />
            ) : (
              <Eye
                size={16}
              />
            )}

            {showPasswords
              ? translateFallback(
                  t,
                  'accountSettings.actions.hidePasswords',
                  'Hide passwords',
                )
              : translateFallback(
                  t,
                  'accountSettings.actions.showPasswords',
                  'Show passwords',
                )}
          </button>

          <div className="account-settings-warning">
            <AlertTriangle
              size={17}
            />

            {translateFallback(
              t,
              'accountSettings.passwordHint',
              'Use at least 8 characters.',
            )}
          </div>

          <button
            type="submit"
            className="account-settings-button account-settings-button--danger"
            disabled={
              savingPassword
            }
          >
            {savingPassword ? (
              <Loader2
                size={17}
              />
            ) : (
              <LockKeyhole
                size={17}
              />
            )}

            {translateFallback(
              t,
              'accountSettings.actions.updatePassword',
              'Update password',
            )}
          </button>
        </form>
      </section>
    </main>
  );
}

export default AccountSettings;